import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import DemoButton from '../Common/DemoButton';
import './WhyShipmnts.css';
import { Workflow, Collaboration, AIGeneratedReport, Tracking } from '../../assets';

const STATS = [
  { value: '6,000+', label: 'Active users' },
  { value: '180+', label: 'Companies' },
  { value: '2015', label: 'Founded' },
  { value: '2–3 wks', label: 'Average onboarding' },
];

const FEATURES = [
  {
    num: '01',
    headline: 'Live in 2-3 weeks: not 3-6 months',
    body: 'Enterprise freight software used to mean a 6-month rollout, a certified admin, and a dedicated IT project. Shipmnts flips this: most SME teams are processing live shipments within 2–3 weeks of signing on. Enterprise teams within 3–4 weeks. Opening balances and invoice history are migrated as part of onboarding: your team is trained and live before the first month is over.',
    bullets: [
      'SME teams live in 2-3 weeks, enterprise in 3-4 weeks',
      'Opening balances and invoice history migrated',
      'No dedicated IT administrator required',
      'Same-day support during go-live week',
      'Parallel run option: operate both systems during transition',
    ],
    img: Workflow,
    imgAlt: 'Shipmnts workflow and milestone management',
    flip: false,
  },
  {
    num: '02',
    headline: 'A workspace your team discovers on their own',
    body: "Every shipment is one screen. Job details, milestones, documents, finance, and customer communication are all connected in a single view: not spread across five tabs. Teams migrating from legacy platforms consistently report self-discovery from day one. Staff who have never seen Shipmnts before navigate independently within hours. There are no training manuals because there don't need to be.",
    bullets: [
      'Single-screen shipment workspace',
      'Workflow 360: full job sequence visible to every team member',
      'One-click generation of BL, AWB, delivery orders',
      'Real-time Control Tower for all active shipments',
      'Exception alerts before problems become delays',
    ],
    img: Tracking,
    imgAlt: 'Shipmnts single-screen shipment tracking',
    flip: true,
  },
  {
    num: '03',
    headline: 'Customers on your platform: not in your inbox',
    body: 'Activity 360 gives your team and your customers a shared platform. Shipment updates, document sharing, in-app messaging, and a branded tracking portal: all in one place. The volume of inbound status calls, emails, and WhatsApp messages drops significantly within the first month for teams that activate it. Your customers see what you see, when you see it.',
    bullets: [
      'Shared shipment workspace for team and customer',
      'Branded customer tracking portal',
      'Document sharing and approval in-app',
      'In-app messaging replaces email and WhatsApp chains',
      'Automated milestone notifications at every stage',
    ],
    img: Collaboration,
    imgAlt: 'Shipmnts Activity 360 customer collaboration',
    flip: false,
  },
  {
    num: '04',
    headline: 'Analytics your management team will actually open',
    body: 'Standard freight software gives you reports nobody reads. Shipmnts ships a native analytics layer configurable by branch, by salesperson, and by business vertical: no Excel exports, no BI tool, no waiting for the finance team to pull numbers. Every level of your business gets dashboards built for their role: finance sees receivables and payables, sales sees pipeline and win rates, operations sees exceptions and demurrage.',
    bullets: [
      'Branch-wise P&L, operations, and finance reports',
      'Salesperson-wise performance, pipeline health, and churn',
      'Business vertical-wise revenue and margin analysis',
      'Automated daily shipment summary (DSR) reports',
      'Configurable: no data limits, no fixed dimensions',
    ],
    img: AIGeneratedReport,
    imgAlt: 'Shipmnts configurable analytics dashboard',
    flip: true,
  },
  {
    num: '05',
    headline: 'Built for where you operate: not adapted for it',
    body: 'Most freight ERP platforms are built for one market and then stretched to cover others. Shipmnts builds market-specific compliance depth natively: India, UAE, and US each have purpose-built workflows, not bolt-on localisation.',
    marketCards: [
      {
        flag: '🇮🇳',
        market: 'India',
        items: [
          'GST (IGST/CGST/SGST) and TDS',
          'Automated GSTR2B reconciliation',
          'ICEGATE status tracking',
          'MTO and CHA-specific workflows',
          'e-invoicing built in',
        ],
      },
      {
        flag: '🇦🇪',
        market: 'UAE',
        items: [
          'Mirsal 2: all 38 declaration types',
          'Dubai Trade Portal integration',
          'JAFZA and free zone workflows',
          'UAE VAT (0%/5%) auto-calculated',
          'MPCI pre-loading compliance',
        ],
      },
      {
        flag: '🇺🇸',
        market: 'US',
        items: [
          'Air & ocean operations today',
          'NVOCC: HBL/MBL/AWB workflows',
          'Integrated finance in USD',
          'AMS/ISF via ACE: roadmap 2026',
          'No per-transaction fees',
        ],
      },
    ],
    flip: false,
  },
];

export default function WhyShipmnts() {
  return (
    <>
      <Helmet>
        <title>Why Shipmnts: Purpose-Built Freight Forwarding Software</title>
        <meta
          name="description"
          content="Why freight forwarders choose Shipmnts: live in 2-3 weeks, single-screen workspace, Activity 360 collaboration, configurable analytics, and deep compliance for India, UAE, and the US."
        />
      </Helmet>

      <div className="wy-page">
        {/* ── Hero ── */}
        <section className="wy-hero">
          <div className="wy-container">
            <p className="wy-hero-eyebrow">Why Shipmnts</p>
            <h1 className="wy-hero-title">
              Purpose-built for freight forwarding.
              <br />
              <em>Not adapted from something else.</em>
            </h1>
            <p className="wy-hero-sub">
              Shipmnts is designed from the ground up for freight forwarders: faster to start,
              easier to use, and deep enough to run a multi-branch, multi-country operation without
              a second thought.
            </p>
            <div className="wy-hero-actions">
              <DemoButton label="Book a Demo" />
              <Link to="/about" className="wy-hero-link">
                Our story
              </Link>
            </div>
          </div>
        </section>

        {/* ── Stats ── */}
        <section className="wy-stats">
          <div className="wy-container">
            <div className="wy-stats-grid">
              {STATS.map(({ value, label }) => (
                <div key={label} className="wy-stat">
                  <span className="wy-stat-value">{value}</span>
                  <span className="wy-stat-label">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Feature sections ── */}
        <section className="wy-features">
          <div className="wy-container">
            {FEATURES.map((f) => (
              <div
                key={f.num}
                className={`wy-feature${f.flip ? ' wy-feature--flip' : ''}${f.marketCards ? ' wy-feature--full' : ''}`}
              >
                {/* Text side */}
                <div className="wy-feature-text">
                  <span className="wy-feature-num">{f.num}</span>
                  <h2 className="wy-feature-title">{f.headline}</h2>
                  <p className="wy-feature-body">{f.body}</p>
                  {f.bullets && (
                    <ul className="wy-feature-bullets">
                      {f.bullets.map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                  )}
                  {f.marketCards && (
                    <div className="wy-market-cards">
                      {f.marketCards.map(({ flag, market, items }) => (
                        <div key={market} className="wy-market-card">
                          <p className="wy-market-card-title">
                            {flag} {market}
                          </p>
                          <ul className="wy-market-card-list">
                            {items.map((item) => (
                              <li key={item}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Visual side */}
                {f.img && (
                  <div className="wy-feature-visual">
                    <div className="wy-feature-img-wrap">
                      <img src={f.img} alt={f.imgAlt} className="wy-feature-img" />
                    </div>
                  </div>
                )}
                {f.marketCards && !f.img && (
                  <div className="wy-feature-visual wy-feature-visual--hidden-desktop" />
                )}
              </div>
            ))}
          </div>
        </section>

        {/* ── Support callout ── */}
        <section className="wy-support">
          <div className="wy-container">
            <div className="wy-support-inner">
              <div className="wy-support-text">
                <h2 className="wy-support-title">Support that closes tickets before end of day</h2>
                <p className="wy-support-body">
                  Most freight software vendors treat support as a cost centre: ticket queues,
                  48-hour SLAs, and support teams in different time zones. Shipmnts includes in-app
                  chat support with same-day response during your business hours. During go-live
                  week, the same team that onboarded you is on call. No extra charge, no support
                  tier to upgrade to.
                </p>
                <ul className="wy-feature-bullets">
                  <li>In-app chat: no email ticketing</li>
                  <li>Same-day first response during business hours</li>
                  <li>Dedicated support through the first 30 days</li>
                  <li>Live training sessions tailored to your workflows</li>
                </ul>
              </div>
              <div className="wy-support-quote">
                <blockquote className="wy-blockquote">
                  <p>
                    "The onboarding team responded within the hour on go-live day. Every question
                    our operations staff had was answered before end of day. That's not something
                    we'd experienced with any previous vendor."
                  </p>
                  <footer>
                    <strong>Operations Head</strong>
                    <span>Freight Forwarder, Mumbai</span>
                  </footer>
                </blockquote>
              </div>
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="wy-cta">
          <div className="wy-container">
            <div className="wy-cta-inner">
              <h2 className="wy-cta-title">See why freight forwarders switch to Shipmnts</h2>
              <p className="wy-cta-sub">
                Live in 2-3 weeks. No IT admin required. Same-day support from day one.
              </p>
              <div className="wy-cta-actions">
                <DemoButton label="Book a Demo" />
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
